// Module 10 (viz) collaborator — the SELECTION subsystem, extracted from the adapter (P10 / task #136,
// the deferred adapter.ts cut). Owns the selection-ring marker + the parcel-border outline (both pool-of-1,
// `selection-marker.ts` / `selection-outline.ts`), the per-object ANCHOR lookups (edge mid-vertex + parcel
// rings), and the `lastSel` shallow-diff so a re-select writes the GPU only on change. The adapter keeps
// the two raw Cesium collections (it creates/adds/lifts/disposes them) and injects them here at init — so
// the GPU-resource lifecycle is unchanged; only the selection LOGIC moves. Behaviour-PRESERVING. Same
// module: imports its own (marker/outline/arc helpers) + state + the cesium-api seam (CLAUDE.md boundary).
import type { UiState } from "../state/index";
import type { CesiumLike, PointPrimitiveCollectionLike, PolylineCollectionLike } from "./cesium-api";
import { makeSelectionMarker } from "./selection-marker";
import type { SelectionMarker } from "./selection-marker";
import { makeSelectionOutline } from "./selection-outline";
import type { SelectionOutline } from "./selection-outline";
import { arcEndpoints } from "./overlay-arcs";
import { ballisticArc } from "./arc-geometry";

type Ring = readonly { lon: number; lat: number }[];

export interface SelectionDeps {
  cesium: CesiumLike;
  markerPoints: PointPrimitiveCollectionLike; // the selection-ring pool (created + owned by the adapter)
  selOutlineLines: PolylineCollectionLike; // the parcel-border pool (created + owned by the adapter)
  posOf: (id: string) => readonly [number, number] | undefined; // node id → [lon,lat] (the adapter's index)
}

export class SelectionController {
  private marker: SelectionMarker | null = null;
  private outline: SelectionOutline | null = null;
  private readonly edgeMid = new Map<string, unknown>(); // edge id → polyline mid-vertex Cartesian3 (ring anchor)
  private readonly parcelRingById = new Map<string, Ring>(); // node id → its footprint ring (border highlight)
  private wallRingsSeeded = false; // neighbourhood parcel rings captured once (static geometry)
  private lastSel: UiState["selection"] = undefined; // shallow-diff: write the GPU only on a selection change

  // mount: build the marker + outline over the adapter's raw collections. The marker's ANCHOR resolver —
  // edge → its polyline mid-vertex; a clicked info/finance PACKET (synthetic from→to id) → the apex of the
  // SAME ballistic arc the overlay draws; node → its true coord — moves here intact.
  init(d: SelectionDeps): void {
    const { cesium, markerPoints, selOutlineLines, posOf } = d;
    this.marker = makeSelectionMarker(cesium, markerPoints, (sel) => {
      if (sel.kind === "edge") {
        const mid = this.edgeMid.get(sel.id);
        if (mid) return mid; // a real electrical edge → its polyline mid-vertex
        const ep = arcEndpoints(sel.id); // a clicked packet (from→to) → halo on its arc apex
        if (ep) {
          const a = posOf(ep.from);
          const b = posOf(ep.to);
          if (a && b) {
            const arc = ballisticArc(a, b, { apexScale: 0.15 });
            const apexH = arc.heights[Math.floor(arc.heights.length / 2)] ?? 1400;
            return cesium.Cartesian3.fromDegrees((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, apexH);
          }
        }
        return undefined;
      }
      const ll = posOf(sel.id);
      return ll ? cesium.Cartesian3.fromDegrees(ll[0], ll[1]) : undefined;
    });
    this.outline = makeSelectionOutline(cesium, selOutlineLines, (id) => this.parcelRingById.get(id));
  }

  registerEdgeMid(edgeId: string, midVertex: unknown): void {
    this.edgeMid.set(edgeId, midVertex);
  }

  // neighbourhood parcel rings — captured ONCE (static wall geometry) for the border highlight.
  seedWallRings(walls: readonly { id: string; ring: Ring }[]): void {
    if (this.wallRingsSeeded) return;
    for (const w of walls) this.parcelRingById.set(w.id, w.ring);
    this.wallRingsSeeded = true;
  }

  // a facility parcel's ring, keyed by the resolved node id (joined as footprints are crosswalked).
  seedFacilityRing(nodeId: string, ring: Ring): void {
    this.parcelRingById.set(nodeId, ring);
  }

  // shallow-diff apply (the store subscription path): write the ring + border only on a change. Returns
  // true iff it changed, so the adapter can request a render.
  apply(selection: UiState["selection"]): boolean {
    if (selection === this.lastSel) return false;
    this.lastSel = selection;
    this.marker?.set(selection);
    this.outline?.set(selection);
    return true;
  }

  // the initial mount apply (mirrors the adapter's cur.selection seed: set only when a selection exists).
  set(selection: UiState["selection"]): void {
    this.lastSel = selection;
    if (selection) { this.marker?.set(selection); this.outline?.set(selection); }
  }

  // dispose: release the marker/outline closures (the adapter destroys the raw collections) + reset the
  // border state, matching the inline original (edgeMid persists across re-mounts, re-seeded by buildEdges).
  dispose(): void {
    this.marker = null;
    this.outline = null;
    this.parcelRingById.clear();
    this.wallRingsSeeded = false;
    this.lastSel = undefined;
  }
}
